/**
 * SepaPlansAdminPanel — Panel admin "Paiement en attente / Paiement partiel" (V35.32).
 * Affiche tous les plans SEPA RCD Solidaire (acompte 60%, mandat, échéances, montants).
 * Filtres : pending / partial / failed / all.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, RefreshCw, Shield, CheckCircle2, Banknote, Download, TrendingUp, AlertTriangle, Activity, Search } from 'lucide-react';
import OscopLogo from './brand/OscopLogo';
import GoCardlessRetryHistory from './GoCardlessRetryHistory';
import SepaMandateCreationPanel from './SepaMandateCreationPanel';
import SepaAgendaGlobal from './SepaAgendaGlobal';
import { fmtEUR, fmtDate, KpiCard } from './sepaHelpers';
import SepaPlanCard from './SepaPlanCard';
import SepaCustomizeModal from './SepaCustomizeModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SepaPlansAdminPanel() {
  const { getAuthHeader, user } = useAuth();
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | pending | partial | failed
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState({});
  const [customizePlan, setCustomizePlan] = useState(null);
  const [busy, setBusy] = useState(null); // sepa_plan_id en cours d'action
  const [stats, setStats] = useState(null);
  const [gcAlerts, setGcAlerts] = useState(null); // {alerts, open_count}
  const [ackBusy, setAckBusy] = useState(null);
  const [stripeFailed, setStripeFailed] = useState([]); // échecs rail Stripe (relances)

  const fetchPlans = useCallback(async (silent = false) => {
    try {
      if (silent !== true) setLoading(true);
      const apiFilter = (filter === 'failed' || filter === 'cb') ? '' : filter;
      const plansParams = apiFilter && apiFilter !== 'all' ? `?status=${apiFilter}` : '';
      const [plansRes, statsRes, alertsRes, stripeFailedRes] = await Promise.allSettled([
        axios.get(`${API}/gocardless/admin/plans${plansParams}`, { headers: getAuthHeader() }),
        axios.get(`${API}/gocardless/admin/dashboard-stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/legalcoop/sepa/alerts?only_open=true`, { headers: getAuthHeader() }),
        axios.get(`${API}/sepa-stripe/admin/echeances?filter=failed`, { headers: getAuthHeader() }),
      ]);
      let list = plansRes.status === 'fulfilled' ? (plansRes.value.data?.plans || []) : [];
      if (filter === 'failed') {
        list = list.filter((p) =>
          (p.installments || []).some((i) =>
            ['failed', 'chargedback', 'creation_failed'].includes(i.status)
          )
        );
      }
      if (filter === 'cb') {
        list = list.filter((p) => p.solde_par_lien_cb);
      }
      setPlans(list);
      setStats(statsRes.status === 'fulfilled' ? statsRes.value.data : null);
      setGcAlerts(alertsRes.status === 'fulfilled' ? alertsRes.value.data : null);
      setStripeFailed(stripeFailedRes.status === 'fulfilled' ? (stripeFailedRes.value.data?.echeances || []) : []);
    } catch (e) {
      console.error('Erreur chargement plans SEPA', e);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [filter, getAuthHeader]);

  const acknowledgeAlert = async (alertId) => {
    setAckBusy(alertId);
    try {
      await axios.post(
        `${API}/legalcoop/sepa/alerts/${alertId}/acknowledge`,
        {},
        { headers: getAuthHeader() },
      );
      const res = await axios.get(`${API}/legalcoop/sepa/alerts?only_open=true`, { headers: getAuthHeader() });
      setGcAlerts(res.data);
    } catch (e) {
      console.error('Erreur acquittement alerte SEPA', e);
    } finally {
      setAckBusy(null);
    }
  };

  useEffect(() => { fetchPlans(); }, [fetchPlans]);
  useEffect(() => {
    const onFocus = () => fetchPlans(true);
    const t = setInterval(() => { if (!document.hidden) fetchPlans(true); }, 30000);
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [fetchPlans]);

  const filteredPlans = useMemo(() => {
    if (!searchTerm) return plans;
    const q = searchTerm.toLowerCase().trim();
    return plans.filter((p) => {
      const sub = p.subscriber || {};
      const territoires = Array.isArray(sub.territoires) ? sub.territoires : [];
      const haystack = [
        sub.raison_sociale || '',
        sub.siret || '',
        sub.ville || '',
        sub.code_postal || '',
        p.user_email || '',
        p.souscription_id || '',
        p.gocardless_mandate_id || '',
        p.gocardless_customer_id || '',
        ...territoires.map(String),
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [plans, searchTerm]);

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  // Route admin selon le rail du plan (Stripe pour les nouveaux, GoCardless legacy)
  const adminBase = (plan) => `${API}/${plan.rail === 'stripe' ? 'sepa-stripe' : 'gocardless'}/admin`;

  const handleCancel = async (plan) => {
    if (!window.confirm(`Annuler toutes les échéances non encore prélevées pour la souscription ${plan.souscription_id} ?\n\nCette action est irréversible.`)) return;
    try {
      setBusy(plan.sepa_plan_id);
      const res = await axios.post(`${adminBase(plan)}/plans/${plan.souscription_id}/cancel`, {}, { headers: getAuthHeader() });
      toast.success(`Échéancier annulé · ${res.data?.cancelled_installments?.length || 0} échéance(s)`);
      await fetchPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur annulation échéancier');
    } finally { setBusy(null); }
  };

  const handleRevoke = async (plan) => {
    if (!window.confirm(`Révoquer le mandat SEPA de la souscription ${plan.souscription_id} ?\n\nLe membre sera notifié par email. Plus aucun prélèvement ne sera possible sans nouveau mandat.`)) return;
    try {
      setBusy(plan.sepa_plan_id);
      await axios.post(`${adminBase(plan)}/plans/${plan.souscription_id}/revoke-mandate`, {}, { headers: getAuthHeader() });
      toast.success('Mandat SEPA révoqué et membre notifié');
      await fetchPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur révocation mandat');
    } finally { setBusy(null); }
  };

  const handleRetryInstallment = async (plan, inst) => {
    if (!window.confirm(`Relancer la mensualité #${inst.index} (${fmtEUR(inst.amount_cents)}) ?\n\nUn nouveau prélèvement SEPA sera créé immédiatement.`)) return;
    try {
      setBusy(plan.sepa_plan_id);
      const r = await axios.post(
        `${adminBase(plan)}/plans/${plan.souscription_id}/installments/${inst.index}/retry`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Mensualité #${inst.index} relancée (tentative #${r.data?.retry_count || '?'})`);
      await fetchPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la relance');
    } finally { setBusy(null); }
  };

  const handleToggleAutoRetry = async (plan) => {
    const current = !!plan.auto_retry_disabled;
    const next = !current;
    if (!window.confirm(
      next
        ? `Désactiver l'auto-relance pour ce plan ?\n\nLes échéances en échec ne seront plus relancées automatiquement.\nVous devrez les relancer manuellement.`
        : `Réactiver l'auto-relance pour ce plan ?\n\nLes échéances en échec seront relancées automatiquement (max 2 tentatives, cooldown 3 jours ouvrés).`
    )) return;
    try {
      setBusy(plan.sepa_plan_id);
      const r = await axios.put(
        `${API}/gocardless/admin/plans/${plan.souscription_id}/auto-retry-toggle`,
        {}, { headers: getAuthHeader() }
      );
      toast.success(r.data?.auto_retry_disabled ? 'Auto-relance désactivée' : 'Auto-relance réactivée');
      await fetchPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur toggle auto-retry');
    } finally { setBusy(null); }
  };

  const handleExportCSV = async () => {
    try {
      const params = filter && filter !== 'all' ? `?status_filter=${filter}` : '';
      const r = await axios.get(`${API}/gocardless/admin/plans/export${params}`, {
        headers: getAuthHeader(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(r.data);
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const a = document.createElement('a');
      a.href = url; a.download = `sepa_plans_${filter || 'all'}_${ts}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 3000);
      toast.success('Export CSV téléchargé');
    } catch (e) {
      toast.error(`Export CSV échoué : ${e.message}`);
    }
  };

  const handleCustomizeSubmit = async (formValues) => {
    try {
      setBusy(customizePlan.sepa_plan_id);
      const payload = {};
      if (formValues.nb_echeances) payload.nb_echeances = Number(formValues.nb_echeances);
      if (formValues.offsets_days) payload.offsets_days = formValues.offsets_days.split(',').map((x) => Number(x.trim())).filter(Number.isFinite);
      if (formValues.amounts_eur) payload.amounts_cents = formValues.amounts_eur.split(',').map((x) => Math.round(Number(x.trim()) * 100)).filter(Number.isFinite);
      if (formValues.reference) payload.reference = formValues.reference;
      await axios.put(`${adminBase(customizePlan)}/plans/${customizePlan.souscription_id}/customize`, payload, { headers: getAuthHeader() });
      toast.success('Échéancier personnalisé enregistré');
      setCustomizePlan(null);
      await fetchPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur personnalisation');
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="sepa-admin-panel">
      {/* Bandeau navy O'SCOP */}
      <div className="relative overflow-hidden rounded-2xl shadow-[0_20px_60px_-15px_rgba(10,61,98,0.45)] group/header">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a3d62] via-[#0c4a78] to-[#08304d]" />
        <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cg fill='%23DEA900' fill-opacity='0.5'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E\")" }} />
        <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#DEA900]/15 blur-3xl" />
        <div className="relative p-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#DEA900] to-[#b88600] shadow-lg shadow-[#DEA900]/30 flex items-center justify-center ring-1 ring-[#f5cc3e]/40">
              <OscopLogo size={32} variant="on-navy" />
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-[0.18em] text-[#f5cc3e] uppercase">O'SCOP · RCD Solidaire</div>
              <h2 className="text-2xl font-bold text-white tracking-tight mt-0.5">Paiements RCD — Mandats SEPA</h2>
              <p className="text-sm text-white/70 mt-0.5">Suivi des échéanciers SEPA · Stripe (4 prélèvements) & GoCardless (existants)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleExportCSV}
              disabled={loading || !isSuperAdmin}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/30"
              data-testid="sepa-admin-export-csv"
              title="Exporter le détail des plans en CSV (1 ligne par échéance)"
            >
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button
              onClick={fetchPlans}
              disabled={loading}
              className="bg-[#DEA900] hover:bg-[#f5cc3e] text-[#0a3d62] font-semibold border-0"
              data-testid="sepa-admin-refresh"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualiser
            </Button>
          </div>
        </div>
      </div>

      {/* Création manuelle de mandats SEPA (multi-dispositifs) */}
      <SepaMandateCreationPanel />

      {/* Alertes GoCardless temps réel */}
      {gcAlerts && gcAlerts.open_count > 0 && (
        <div
          className="relative overflow-hidden rounded-2xl border-l-4 border-red-600 bg-gradient-to-r from-red-50 via-red-50 to-rose-50 p-5 shadow-md"
          data-testid="sepa-gc-alerts-banner"
        >
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-red-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-red-900 text-base" data-testid="sepa-gc-alerts-count">
                {gcAlerts.open_count} alerte(s) GoCardless à traiter
              </h3>
              <p className="text-sm text-red-700 mt-1">
                Échecs de prélèvement ou incidents de mandat détectés en temps réel
                (webhook). Un email d&apos;alerte a été envoyé à l&apos;administrateur.
              </p>
              <div className="mt-3 space-y-2">
                {(gcAlerts.alerts || []).map((a) => (
                  <div
                    key={a.id}
                    className="bg-white border border-red-200 rounded-lg p-3 flex items-center justify-between flex-wrap gap-2"
                    data-testid={`sepa-gc-alert-${a.id}`}
                  >
                    <div className="flex-1 min-w-0 text-xs text-slate-700">
                      <span className="inline-block px-2 py-0.5 rounded bg-red-700 text-white text-[10px] font-bold mr-2">
                        {a.label || a.type}
                      </span>
                      <strong>{a.customer_email || a.resource_id}</strong>
                      {a.amount_cents ? <> • {(a.amount_cents / 100).toFixed(2)} €</> : null}
                      {a.cause ? <> • Cause : {a.cause}</> : null}
                      {a.created_at ? <span className="text-slate-400"> • {String(a.created_at).slice(0, 16).replace('T', ' ')}</span> : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => acknowledgeAlert(a.id)}
                      disabled={ackBusy === a.id}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg bg-red-700 text-white hover:bg-red-800 disabled:opacity-50"
                      data-testid={`sepa-gc-alert-ack-${a.id}`}
                    >
                      {ackBusy === a.id ? '…' : 'Marquer traité'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bannière d'alertes : échéances échouées */}
      {stats && (stats.totals?.installments_failed > 0 || stats.totals?.installments_chargedback > 0 || stats.totals?.installments_creation_failed > 0) && (
        <div
          className="relative overflow-hidden rounded-2xl border-l-4 border-rose-500 bg-gradient-to-r from-rose-50 via-rose-50 to-amber-50 p-5 shadow-md"
          data-testid="sepa-admin-alerts-banner"
        >
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-rose-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-rose-900 text-base">
                {(stats.totals.installments_failed + stats.totals.installments_chargedback + stats.totals.installments_creation_failed)} prélèvement(s) à examiner
              </h3>
              <p className="text-sm text-rose-700 mt-1">
                {stats.totals.installments_failed > 0 && <span className="mr-3"><strong>{stats.totals.installments_failed}</strong> échec(s)</span>}
                {stats.totals.installments_chargedback > 0 && <span className="mr-3"><strong>{stats.totals.installments_chargedback}</strong> contestation(s)</span>}
                {stats.totals.installments_creation_failed > 0 && <span><strong>{stats.totals.installments_creation_failed}</strong> erreur(s) de création</span>}
              </p>
              <button
                type="button"
                onClick={() => setFilter('failed')}
                className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-rose-700 hover:text-rose-900 underline"
                data-testid="sepa-admin-jump-to-failed"
              >
                Voir les plans concernés →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cartes KPI */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sepa-admin-kpi-grid">
          <KpiCard
            icon={Shield}
            label="Mandats actifs"
            value={stats.totals.mandates_active || 0}
            sub={`${stats.totals.mandates_pending || 0} en attente · ${stats.totals.mandates_revoked || 0} révoqués`}
            tone="emerald"
            testid="sepa-kpi-active"
          />
          <KpiCard
            icon={CheckCircle2}
            label="Échéances payées"
            value={`${stats.totals.installments_paid || 0} / ${stats.totals.installments_total || 0}`}
            sub={`${stats.amounts?.collected_pct || 0}% du total prévu`}
            tone="sky"
            testid="sepa-kpi-paid"
          />
          <KpiCard
            icon={TrendingUp}
            label="Montant collecté"
            value={fmtEUR(stats.amounts?.paid_cents)}
            sub={`Restant à percevoir : ${fmtEUR(stats.amounts?.remaining_cents)}`}
            tone="gold"
            testid="sepa-kpi-amount"
          />
          <KpiCard
            icon={Activity}
            label="Échéances 30 j à venir"
            value={(stats.upcoming_30d || []).length}
            sub={`${stats.totals.installments_scheduled || 0} planifiée(s) au total`}
            tone="indigo"
            testid="sepa-kpi-upcoming"
          />
        </div>
      )}

      {/* Tableau de bord historique des relances */}
      <GoCardlessRetryHistory />

      {/* Échecs rail Stripe — relances immédiates */}
      {stripeFailed.length > 0 && (
        <div className="bg-white border-l-4 border-[#635bff] rounded-2xl p-5 shadow-sm space-y-3" data-testid="sepa-stripe-failed-block">
          <div>
            <h3 className="font-bold text-[#0a3d62] text-base">Échecs de prélèvements Stripe ({stripeFailed.length})</h3>
            <p className="text-xs text-slate-500">Reprogrammation automatique à J+7 (2 tentatives max). Relance immédiate possible.</p>
          </div>
          <div className="space-y-2">
            {stripeFailed.map((e) => (
              <div
                key={`${e.souscription_id}-${e.index}`}
                className="flex items-center justify-between gap-3 flex-wrap border border-slate-200 rounded-lg px-3 py-2"
                data-testid={`sepa-stripe-failed-${e.souscription_id}-${e.index}`}
              >
                <div className="text-xs text-slate-700 min-w-0">
                  <strong>{e.customer_name || e.customer_email || e.souscription_id}</strong>
                  <span className="text-slate-400"> · Échéance #{e.index} · {fmtEUR(e.amount_cents)} · {fmtDate(e.charge_date)}</span>
                  {e.retry_count ? <span className="ml-1 text-amber-700 font-semibold">· R{e.retry_count}</span> : null}
                  {e.failure_reason ? (
                    <span className="block text-rose-700 truncate max-w-[420px]" title={e.failure_reason}>{e.failure_reason}</span>
                  ) : null}
                </div>
                {isSuperAdmin && (
                  <Button
                    size="sm"
                    onClick={() => handleRetryInstallment(
                      { rail: 'stripe', souscription_id: e.souscription_id, sepa_plan_id: e.souscription_id },
                      { index: e.index, amount_cents: e.amount_cents }
                    )}
                    disabled={busy === e.souscription_id}
                    className="bg-amber-500 hover:bg-amber-600 text-white h-7 px-2 text-[11px]"
                    data-testid={`sepa-stripe-retry-${e.souscription_id}-${e.index}`}
                  >
                    Relancer
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agenda global repliable */}
      <SepaAgendaGlobal />

      {/* Filtres */}
      <div className="bg-white border border-slate-200/80 rounded-2xl p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher par raison sociale, SIRET, email, territoire, ID souscription…"
            className="pl-10"
            data-testid="sepa-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { value: 'all', label: 'Tous les plans', count: filteredPlans.length },
            { value: 'pending', label: 'Paiement en attente' },
            { value: 'partial', label: 'Paiement partiel' },
            { value: 'failed', label: 'Échecs / Contestations' },
            { value: 'cb', label: 'Soldés par lien CB' },
          ].map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              data-testid={`sepa-filter-${f.value}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                filter === f.value
                  ? 'bg-[#0a3d62] text-white shadow-md'
                  : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="ml-auto text-sm text-slate-600 tabular-nums">
            <strong className="text-[#0a3d62]">{filteredPlans.length}</strong> plan(s)
          </span>
        </div>
      </div>

      {/* Liste des plans */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#DEA900] mx-auto" />
        </div>
      ) : filteredPlans.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center" data-testid="sepa-empty">
          <div className="h-16 w-16 mx-auto rounded-2xl bg-[#0a3d62]/5 flex items-center justify-center mb-4">
            <Banknote className="h-8 w-8 text-[#0a3d62]/40" />
          </div>
          <p className="text-slate-700 font-semibold">Aucun plan SEPA correspondant</p>
          <p className="text-xs text-slate-500 mt-1">Les plans apparaîtront ici après signature des mandats.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="sepa-plans-list">
          {filteredPlans.map((plan) => (
            <SepaPlanCard
              key={plan.sepa_plan_id}
              plan={plan}
              isOpen={!!expanded[plan.sepa_plan_id]}
              toggleExpand={toggleExpand}
              isSuperAdmin={isSuperAdmin}
              busy={busy}
              handleRetryInstallment={handleRetryInstallment}
              handleCancel={handleCancel}
              handleRevoke={handleRevoke}
              handleToggleAutoRetry={handleToggleAutoRetry}
              setCustomizePlan={setCustomizePlan}
              onChanged={fetchPlans}
            />
          ))}
        </div>
      )}

      {customizePlan && (
        <SepaCustomizeModal
          plan={customizePlan}
          onClose={() => setCustomizePlan(null)}
          onSubmit={handleCustomizeSubmit}
          busy={busy === customizePlan.sepa_plan_id}
        />
      )}
    </div>
  );
}
